import React, { useEffect, useState } from "react";
import {
  Container,
  Spinner,
  Button,
} from "reactstrap";

import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "react-query";
import useAxios from "../../../../api/useAxios";
import BASE_URL_WITH_PROTOCOL from "../../../../utils/dynamicUrl";
import JobSeekerAbout from "./components/job-seeker-about";
import JobSeekerWorkexperience from "./components/work-experience/job-seeker-workexperience";
import JobSeekerProject from "./components/project/job-seeker-project";
import JobSeekerCertification from "./components/certification/job-seeker-certification";
import JobSeekerTraining from "./components/training/job-seeker-training";
import JobSeekerEducation from "./components/education/job-seeker-education";
import JobSeekerSkills from "./components/skills/job-seeker-skills";
import JobSeekerDescription from "./components/description/job-seeker-description-component";
import { dummyUser } from "../../../../assets/images";
import { Resume } from "../../../../utils/resume-download";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from 'file-saver';

const CandidateDetailPage = () => {
  const params = useParams();
  const candidateSlug = params?.id;
  let baseURL = `${BASE_URL_WITH_PROTOCOL}`;
  const api = useAxios();
  const [logoLink, setLogoLink] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const { data: profileData, isLoading } = useQuery(
    ["candidate-profile", candidateSlug],
    () => api.get(`/candidate/candidate/${candidateSlug}`).then((res) => res.data),
    { enabled: Boolean(candidateSlug), retry: 1, staleTime: 5 * 60 * 1000 }
  );

  const candidateID = profileData?.profile?.id;

  const handleDownloadResume = async () => {
    if (!candidateID) return;

    setIsDownloading(true);
    try {
      const response = await api.get(`/candidate/resume-data/${candidateID}`);
      const profileData = response?.data;
      if (!profileData) throw new Error("No data found for candidate");

      const blob = await pdf(<Resume profileData={profileData} />).toBlob();

      saveAs(blob, `${profileData.name || 'candidate'}_CV.pdf`);
    } catch (error) {
      console.error("Error generating resume PDF", error);

    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    document.title = "Job Seeker | Search O Pal";
  }, []);

  useEffect(() => {
    try {
      if (profileData?.profile?.["profile_picture"]) {
        if (process.env.NODE_ENV == "production") {
          let parsedUrl = new URL(profileData?.profile?.["profile_picture"]);

          parsedUrl.search = "";

          let urlWithoutParams = parsedUrl.toString();
          setLogoLink(`${urlWithoutParams}`);
        } else {
          setLogoLink(`${baseURL}${profileData?.profile["profile_picture"]}`);
        }
      } else {
        setLogoLink("/images/users/user-dummy.png");
      }
    } catch (error) {
      console.error("Error parsing profile picture URL:", error);
    }
  }, [profileData]);

  

  return (
    <div className="page-content">
      <Container className="mt-0 mb-3 p-3 px-4">
        <div className="text-end mb-3">
          <Button color="primary" onClick={() => handleDownloadResume()} disabled={isDownloading}>Download CV</Button>
        </div>
        <div className="row candidate-profile d-flex justify-content-between border rounded border-primary mt-0 mb-3">
          <div className="col">
            <div className="d-flex align-items-start flex-column">
              <span className="d-flex align-items-end">
                {profileData?.profile["profile_picture"] == null ? <img src={dummyUser} alt="Avatar" style={{ width: "30%", borderRadius: "50%" }} /> : (
                  <img
                    src={logoLink}
                    className="rounded-circle avatar-lg img-thumbnail"
                    alt=""
                  />
                )}

              </span>
              <h2 className="mt-3 fw-bold">{profileData?.profile?.name}</h2>
            </div>
          </div>

          <div className="d-flex flex-column justify-content-start col-auto text-start job-seeker-detail-action">

            <div className="align-self-end">
              <h4 className="fw-normal">
                <i className="fa-solid fa-envelope me-2"></i>
                {profileData?.profile?.email}
              </h4>

              <h4 className="fw-normal">
                <i className="fa-solid fa-phone me-2"></i>
                {profileData?.profile?.phone}
              </h4>

              {profileData?.profile?.designation && (
                <h4 className="fw-normal">
                  <i className="fa fa-briefcase me-1"></i>
                  {profileData.profile.designation}
                </h4>
              )}

              {(profileData?.profile?.city || profileData?.profile?.country) && (
                <h4 className="fw-normal">
                  <i className="fa fa-map-pin me-1"></i>
                  {`${profileData.profile.city ? profileData.profile.city + ', ' : ''}${profileData.profile.country}`}
                </h4>
              )}

              {profileData?.profile?.website && (
                <h4 className="fw-normal">
                  <i className="fa fa-globe me-1"></i>
                  {profileData.profile.website}
                </h4>
              )}
            </div>
          </div>
        </div>

        <JobSeekerAbout profileData={profileData} candidateID={candidateID} />

        {profileData?.work_experience?.length > 0 &&
          <>
            <h4 className="text-start mt-3">Work Experience</h4>
            {profileData.work_experience.map((row, i) => <JobSeekerWorkexperience key={i} data={row} />)}
          </>}

        {profileData?.projects?.length > 0 &&
          <>
            <h4 className="text-start mt-3">Projects</h4>
            <div className="border border-primary rounded px-3">
              {profileData.projects.map((row, i) => <JobSeekerProject key={i} data={row} />)}
            </div>
          </>}

        {profileData?.certifications?.length > 0 &&
          <>
            <h4 className="text-start mt-3">Certifications</h4>
            <div className="border border-primary rounded px-3">
              {profileData.certifications.map((row, i) => <JobSeekerCertification key={i} data={row} />)}
            </div>
          </>}

        {profileData?.trainings?.length > 0 &&
          <>
            <h4 className="text-start mt-3">Trainings</h4>
            <div className="border border-primary rounded px-3">
              {profileData.trainings.map((row, i) => <JobSeekerTraining key={i} data={row} />)}
            </div>
          </>}

        {profileData?.education?.length > 0 &&
          <>
            <h4 className="text-start mt-3">Educations</h4>
            <div className="border border-primary rounded px-3">
              {profileData.education.map((row, i) => <JobSeekerEducation key={i} data={row} />)}
            </div>
          </>}

        {profileData?.skills?.length > 0 &&
          <>
            <h4 className="text-start mt-3">Skills</h4>
            <div className="border border-primary rounded p-3">
              {profileData.skills.map((row, i) => <JobSeekerSkills key={i} skill={row} />)}
            </div>
          </>}

        {profileData?.extra_curricular?.length > 0 &&
          <>
            <h4 className="text-start mt-3">Extra Curricular</h4>
            <div className="border border-primary rounded p-3">
              {profileData.extra_curricular.map((row, i) => <JobSeekerDescription key={i} row={row} />)}
            </div>
          </>}

        {profileData?.publications?.length > 0 &&
          <>
            <h4 className="text-start mt-3">Publications</h4>
            <div className="border border-primary rounded p-3">
              {profileData.publications.map((row, i) => <JobSeekerDescription key={i} row={row} />)}
            </div>
          </>}

        {profileData?.references?.length > 0 &&
          <>
            <h4 className="text-start mt-3">References</h4>
            <div className="border border-primary rounded p-3">
              {profileData.references.map((row, i) => <JobSeekerDescription key={i} row={row} />)}
            </div>
          </>}

        {profileData?.achievements?.length > 0 &&
          <>
            <h4 className="text-start mt-3">Achievements</h4>
            <div className="border border-primary rounded p-3">
              {profileData.achievements.map((row, i) => <JobSeekerDescription key={i} row={row} />)}
            </div>
          </>}

        {profileData?.volunteer_work?.length > 0 &&
          <>
            <h4 className="text-start mt-3">Volunteer Work</h4>
            <div className="border border-primary rounded p-3">
              {profileData.volunteer_work.map((row, i) => <JobSeekerDescription key={i} row={row} />)}
            </div>
          </>}

          {/* {profileData?.languages?.length > && <LanguagesSections candidateID={candidateID} />} */}
      </Container>
    </div>
  );
};

export default CandidateDetailPage;